import { AppConfig } from "../../contexts/AppConfigContext";
import { SSEEventCallback } from "./SSE.domain";

export class SSEModel {
  private apiBase: string;
  private eventSources: Map<string, EventSource> = new Map();
  private eventListeners: Map<string, Set<SSEEventCallback>> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private maxReconnectAttempts = 5;
  private reconnectTimeout: Map<string, number> = new Map();

  constructor(config: AppConfig) {
    this.apiBase = config.assignementsAPI;
  }

  /**
   * Initializes the SSE connection for a specific topic.
   * @param topic - Topic to listen to (assignments, providers, etc.)
   * @returns EventSource instance
   */
  initEventSource(topic: string = "assignments"): EventSource | null {
    const attempts = this.reconnectAttempts.get(topic) || 0;

    if (attempts >= this.maxReconnectAttempts) {
      return null;
    }

    if (this.eventSources.has(topic)) {
      const existingSource = this.eventSources.get(topic);
      if (existingSource) {
        existingSource.close();
      }
    }

    try {
      const url = `${this.apiBase}/v1/events?topic=${encodeURIComponent(topic)}`;

      const eventSource = new EventSource(url);

      eventSource.onopen = () => {
        this.reconnectAttempts.set(topic, 0);
        this.reconnectTimeout.set(topic, 5000);
      };

      eventSource.onerror = (error) => {
        if (this.eventSources.has(topic)) {
          const source = this.eventSources.get(topic);
          if (source) {
            source.close();
          }
          this.eventSources.delete(topic);
        }

        const currentAttempts = this.reconnectAttempts.get(topic) || 0;
        this.reconnectAttempts.set(topic, currentAttempts + 1);

        const currentTimeout = this.reconnectTimeout.get(topic) || 5000;
        const newTimeout = Math.min(30000, currentTimeout * 1.5);
        this.reconnectTimeout.set(topic, newTimeout);

        setTimeout(() => {
          this.initEventSource(topic);
        }, newTimeout);
      };

      eventSource.addEventListener("connected", (event) => {
        try {
          const data = JSON.parse(event.data);

          if (this.eventListeners.has("connected")) {
            const listeners = this.eventListeners.get("connected");
            if (listeners) {
              listeners.forEach((callback) => {
                try {
                  callback(data);
                } catch (error) {}
              });
            }
          }
        } catch (error) {}
      });

      eventSource.addEventListener("update", (event) => {
        try {
          const data = JSON.parse(event.data);

          const entityType = data.entity || "unknown";

          if (this.eventListeners.has(entityType)) {
            const listeners = this.eventListeners.get(entityType);
            if (listeners) {
              listeners.forEach((callback) => {
                try {
                  callback(data);
                } catch (error) {}
              });
            }
          }

          if (this.eventListeners.has("all")) {
            const listeners = this.eventListeners.get("all");
            if (listeners) {
              listeners.forEach((callback) => {
                try {
                  callback(data);
                } catch (error) {}
              });
            }
          }
        } catch (error) {}
      });

      this.eventSources.set(topic, eventSource);

      return eventSource;
    } catch (error) {
      return null;
    }
  }

  /**
   * Adds an event listener.
   * @param entityType - Entity type (ASSIGNMENT, or 'all' for all events)
   * @param callback - Callback invoked when an event is received
   * @param topic - Topic to listen to (default: 'assignments')
   */
  addEventListener(
    entityType: string,
    callback: SSEEventCallback,
    topic: string = "assignments",
  ): void {
    if (!this.eventListeners.has(entityType)) {
      this.eventListeners.set(entityType, new Set());
    }

    const listeners = this.eventListeners.get(entityType);
    if (listeners) {
      listeners.add(callback);
    }

    if (!this.eventSources.has(topic)) {
      this.initEventSource(topic);
    }
  }

  /**
   * Removes an event listener.
   * @param entityType - Entity type
   * @param callback - Callback to remove
   */
  removeEventListener(entityType: string, callback: SSEEventCallback): void {
    if (this.eventListeners.has(entityType)) {
      const listeners = this.eventListeners.get(entityType);
      if (listeners) {
        listeners.delete(callback);

        if (listeners.size === 0) {
          this.eventListeners.delete(entityType);
        }

        if (this.eventListeners.size === 0) {
          this.closeAllEventSources();
        }
      }
    }
  }

  /**
   * Closes a specific SSE connection.
   * @param topic - Topic to close (if omitted, closes all)
   */
  closeEventSource(topic?: string): void {
    if (topic) {
      if (this.eventSources.has(topic)) {
        const source = this.eventSources.get(topic);
        if (source) {
          source.close();
        }
        this.eventSources.delete(topic);
      }
    } else {
      this.closeAllEventSources();
    }
  }

  /**
   * Closes all SSE connections.
   */
  private closeAllEventSources(): void {
    this.eventSources.forEach((source) => {
      source.close();
    });
    this.eventSources.clear();
    this.eventListeners.clear();
    this.reconnectAttempts.clear();
    this.reconnectTimeout.clear();
  }
}
